import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import { MoreVert } from "@mui/icons-material";
import { Box, Card, CardContent, Typography } from "@mui/material";

import { ActionButton } from "components/ActionButton";
import { FrmSelectFromObjectsWithHoverHelp } from "components/formFields/FrmSelectFromObjectsWithHoverHelp";
import { FrmSelectScalar } from "components/formFields/FrmSelectScalar";
import { FrmAddFile } from "components/formFields/frm/FrmAddFile";
import { FrmLocation } from "components/formFields/frm/FrmLocation";
import { DatePicker } from "components/html/Input/DatePicker";
import { FormErrors } from "components/utility/FormErrors";
import { HoverHelp } from "components/utility/HoverHelp";
import { VSpace } from "components/utility/VSpace";

import { i18n } from "services/i18nService";
import {
  importSheet,
  MULTIPLE_WORKSHEETS,
} from "services/sosInventoryService/sosApi";
import { isImportableExcelFile } from "services/utility/files";
import { tableSortJs } from "services/utility/reports";

import { useErrors } from "hooks/useErrors";
import { usePlans } from "hooks/usePlans";

import { beforeSave } from "views/Import/beforeSave";
import { isValid } from "views/Import/isValid";
import {
  IMPORT_OPTIONS,
  COMPANION_IMPORT_OPTIONS,
  EMPTY_RECORD,
} from "views/Import/schema";
import { tableStyles } from "views/Reports/tableStyling";

import { openAlert } from "globalState/alertSlice";
import {
  loadingIndicatorOn,
  loadingIndicatorOff,
} from "globalState/loadingSlice";

import { OBJECT_TYPES } from "appConstants";

export function Import() {
  const dispatch = useDispatch();

  const { errors, setErrors, isInError } = useErrors();
  const [record, setRecord] = useState(EMPTY_RECORD);
  const [preview, setPreview] = useState({
    head: null,
    body: null,
    moreRows: null,
  });
  const [worksheets, setWorksheets] = useState(null);
  const { head, body } = preview;

  const { hasAtLeastPlusPlan } = usePlans();
  const reportTable = useRef();

  useEffect(() => {
    if (reportTable.current) {
      tableSortJs(reportTable.current);
    }
  }, [body]);

  function handleFileUpdate(fn) {
    setRecord(fn);
    setPreview({ head: null, body: null, moreRows: null });
    setWorksheets(null);
    if (record.selectedWorksheet) {
      setRecord((prevRecord) => ({ ...prevRecord, selectedWorksheet: null }));
    }
  }

  function handleWorksheetUpdate(_, value) {
    setPreview({ head: null, body: null, moreRows: null });
    setRecord((prevRecord) => ({ ...prevRecord, selectedWorksheet: value }));
  }

  function handleInputFieldChange(fieldName, value) {
    setRecord((prevRecord) => ({ ...prevRecord, [fieldName]: value }));
  }

  async function previewImport() {
    const errors = isValid(record);
    if (errors.messages.length > 0) {
      setErrors(errors);
      return;
    }
    dispatch(loadingIndicatorOn());
    const { id } = record.objectType;
    const postBody = beforeSave(record);
    const { data, success, message } = await importSheet(id, {
      ...postBody,
      action: "preview",
    });
    if (success) {
      const {
        message: dataMessage,
        worksheets: options,
        tBody,
        tHead,
        moreRows,
      } = data;
      if (dataMessage === MULTIPLE_WORKSHEETS) {
        dispatch(
          openAlert({ type: "info", message: i18n("alert.MutipleWorksheets") })
        );
        setWorksheets(options);
      } else {
        const head = tHead || "";
        const body = tBody || "";
        setPreview({ head, body, moreRows });
      }
    } else {
      const errorMessage = message || i18n("alert.ErrorPreviewingDataImport");
      dispatch(openAlert({ type: "error", message: errorMessage }));
    }

    dispatch(loadingIndicatorOff());
  }

  async function importData() {
    if (errors.messages.length > 0) {
      setErrors(errors);
      return;
    }
    dispatch(loadingIndicatorOn());
    const { id } = record.objectType;
    const body = beforeSave(record);
    const { success, message } = await importSheet(id, {
      ...body,
      action: "import",
    });
    if (success) {
      const message = i18n("import.ImportingYourData");
      dispatch(openAlert({ type: "success", message }));
      setPreview({ head: null, body: null, moreRows: null });
      setRecord(EMPTY_RECORD);
      setWorksheets(null);
    } else {
      dispatch(openAlert({ type: "error", message }));
    }
    dispatch(loadingIndicatorOff());
  }

  return (
    <>
      <Box
        sx={{ display: "grid", minWidth: "1070px", ...tableStyles }}
        m={2}
        mb={0}
      >
        <Box mb={1}>
          <Typography variant="h2">{i18n("import.DataImport")}</Typography>
        </Box>
        <Card variant="outlined" sx={{ maxWidth: 500 }}>
          <CardContent>
            <FormErrors errors={errors} setErrors={setErrors} />
            <FrmSelectFromObjectsWithHoverHelp
              selectProps={{
                name: "objectType",
                minWidth: "16rem",
                label: i18n("frmLabel.Importing"),
                options: hasAtLeastPlusPlan
                  ? IMPORT_OPTIONS
                  : COMPANION_IMPORT_OPTIONS,
                value: record.objectType,
                onValueChange: handleInputFieldChange,
                error: isInError("objectType"),
              }}
              helpText={i18n("hoverHelp.Importing")}
            />
            {record.objectType?.id === OBJECT_TYPES.ITEM.fullString && (
              <>
                <FrmLocation
                  disableClearable={false}
                  value={record.location}
                  onValueChange={handleInputFieldChange}
                  placeholder={i18n("alert.DefaultIsAllLocations")}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <DatePicker
                    name="date1"
                    value={record.date1}
                    label={i18n("frmLabel.AsOf")}
                    onValueChange={handleInputFieldChange}
                  />
                  <HoverHelp
                    title={i18n("hoverHelp.ImportAsOf")}
                    sx={{ marginTop: "0.3em" }}
                  />
                </div>
              </>
            )}

            <FrmAddFile
              record={record}
              name="uploadFile"
              setRecord={handleFileUpdate}
              setErrors={setErrors}
              error={isInError("uploadFile")}
              validator={isImportableExcelFile}
              highlightFileName
            />
            {worksheets && (
              <>
                <VSpace space={1} />
                <FrmSelectScalar
                  name="selectedWorksheet"
                  label={i18n("frmLabel.Worksheet")}
                  value={record.worksheet}
                  onValueChange={handleWorksheetUpdate}
                  options={worksheets}
                  disableClearable
                  error={!record.selectedWorksheet}
                />
              </>
            )}
            <VSpace space={2} />
            {preview.head ? (
              <ActionButton onClick={importData}>
                {i18n("import.Import")}
              </ActionButton>
            ) : (
              <ActionButton
                disabled={
                  !record.uploadFile ||
                  (worksheets && !record.selectedWorksheet)
                }
                onClick={previewImport}
              >
                {i18n("import.Preview")}
              </ActionButton>
            )}
          </CardContent>
        </Card>
        <VSpace space={2} />
        {(head || body) && (
          <table
            className="table-sort"
            style={{
              overflowX: "scroll",
              width: "100%",
              wordWrap: "break-word",
            }}
            ref={reportTable}
          >
            <thead
              data-testing="reportTableHeader"
              dangerouslySetInnerHTML={{ __html: head }}
            />
            <tbody
              data-testing="reportTableBody"
              dangerouslySetInnerHTML={{ __html: body }}
            />
          </table>
        )}
        {preview.moreRows && preview.moreRows > 0 && (
          <Box
            sx={{
              mt: 2,
              mb: 2,
              justifyContent: "center",
              display: "flex",
              alignItems: "center",
            }}
          >
            <MoreVert color="primary" sx={{ fontSize: "40px" }} />{" "}
            <Typography sx={{ fontSize: "16px" }}>
              {preview.moreRows} {i18n("import.moreRows")}
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
}
